import { PlanClassification, PlanIds } from '../data/account';
import Communication from './Communication';
import Session from './Session';

/**
 * Subset of a billing plan record that the frontend actually reads.
 * Backend returns more fields; we intentionally type only the ones in use
 * so new server fields don't force a type update.
 */
export interface Plan {
	PlanId: number;
	Sku: number;
	Name: string;
	Price: number;
	PricePeriod?: string;
	Summary?: string;
	DisplayName?: string;
	Tagline?: string;
	InheritLabel?: string;
	LearnMoreUrl?: string;
	MaxAllowedUsers?: number;
	NumberOfLocations?: number;
	AdditionalUserFee?: number;
	StampsPrintLimit?: number;
	StampFees?: unknown;
	[key: string]: unknown;
}

/** Contains utilities for Billing Plans. */
class BillingPlans {
	static plans: Plan[] = [];
	static pendingPlanWasAutoSelectedOnRegistration = false;

	static async init(): Promise<void> {
		const response = await (Communication as any).billingPlans();

		if ((Communication as any).isSuccessful(response)) {
			const { data = {} } = response;
			this.plans = (data.BillingPlans ?? []) as Plan[];
			this.pendingPlanWasAutoSelectedOnRegistration =
				!!data.PendingPlanWasAutoSelectedOnRegistration;

			for (const plan of this.plans) {
				plan.PlanId = this.getPlanId(plan.Sku);
			}
		}
	}

	static getPlanId(planSku: number): number {
		return planSku - 1020000;
	}

	/**
	 * Pretty-prints a plan name
	 * @param planName
	 * @returns Pretty-printed string representation of the plan
	 */
	static prettyPrintPlan(planName = ''): string {
		return planName
			.replace(/\s*-\s*id\s+\d+$/i, '')
			.replace(/\s+\d+$/, '')
			.replace(/-([a-z])/g, (_match, p1: string) => `-${p1.toUpperCase()}`);
	}

	static getCurrentPlan(): Plan | undefined {
		const { planDetails = {} } = (Session as any).getAccountInfo();
		const { planId } = planDetails as { planId?: number };
		let currentPlan: Plan | undefined;

		this.plans.forEach(plan => {
			if (plan.PlanId === planId) {
				currentPlan = plan;
			}
		});
		return currentPlan;
	}

	static getPlanByPlanId(planId: number): Plan | undefined {
		let plan: Plan | undefined;
		this.plans.forEach(record => {
			if (record.PlanId === planId) {
				plan = record;
			}
		});
		return plan;
	}

	// Gets the user's current active plan (either pending or current)
	// returns the active plan object
	static getCurrentActivePlan(): Plan | undefined {
		const { planDetails = {} } = (Session as any).getAccountInfo();
		const { pendingPlanId: planSku } = planDetails as {
			pendingPlanId?: number;
		};

		if (!planSku) {
			return this.getCurrentPlan();
		}

		const pendingPlanId = this.getPlanId(planSku);

		return this.getPlanByPlanId(pendingPlanId) || this.getCurrentPlan();
	}

	// Finds the next available upgrade plan based on price
	// returns the next higher-priced plan or null if none exists
	static getNextUpgradePlan = (): Plan | null => {
		const activePlan = this.getCurrentActivePlan();
		if (!activePlan) return null;
		const sortedPlans = [...this.plans].sort((a, b) => a.Price - b.Price);

		return (
			sortedPlans.find(plan => plan && activePlan.Price < plan.Price) || null
		);
	};

	static isUserInEcommercePlan = (): boolean => {
		const { planDetails = {} } = (Session as any).getAccountInfo();
		const pd = planDetails as { planId?: number; pendingPlanId?: number };
		const pendingPlanId = pd.pendingPlanId ? pd.pendingPlanId - 1020000 : null;

		return (
			pd.planId === PlanIds.Ecommerce ||
			pd.planId === PlanIds.EcommerceV383 ||
			pendingPlanId === PlanIds.Ecommerce ||
			pendingPlanId === PlanIds.EcommerceV383
		);
	};

	static isUserInCoreOfficeEssentialPlan = (): boolean => {
		const { planDetails = {} } = (Session as any).getAccountInfo();
		const pd = planDetails as { planId?: number };

		const coreOfficeEssential: number[] = [
			PlanIds.Core,
			PlanIds.CoreV380,
			PlanIds.Office,
			PlanIds.OfficeV381,
			PlanIds.Essential,
		];
		return (
			!!pd.planId &&
			coreOfficeEssential.indexOf(pd.planId) !== -1 &&
			!this.isUserInEcommercePlan()
		);
	};

	static isUserInPaygPlan = (): boolean => {
		const { planDetails = {} } = (Session as any).getAccountInfo();
		return (planDetails as { planId?: number }).planId === PlanIds.PAYG;
	};

	static isNewLineupPlan = (): boolean => {
		const { planClassificationId } = (Session as any).getAccountInfo() as {
			planClassificationId?: string;
		};
		return planClassificationId === PlanClassification.NewLineup;
	};

	/**
	 * Is this a trial plan? Pass a `planId` to check a specific plan; omit it
	 * to check the signed-in user's current plan.
	 */
	static isNewLineUpTrialPlan = (planId?: number | null): boolean => {
		const id =
			planId ??
			((Session as any).getAccountInfo().planDetails as { planId?: number })
				?.planId;
		return id === PlanIds.Trial401;
	};
}

export default BillingPlans;
